import React, { useMemo } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { BillingCustomer, Plan, PricePeriod } from '../../../models/Plan'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { ChangePaymentInfoButton } from '../ChangePaymentInfoButton'

interface PlanUpgradeBodyProps {
  customer: BillingCustomer
  plan: Plan
  plans: Plan[]
  period: PricePeriod
  paymentType: string
  autoChargeDisabled: boolean
  onPlanChange: (plan: Plan) => void
  onPaymentTypeChange: (paymentType: string) => void
}

const PAYMENT_TYPE_LABELS = {
  autoCharge: 'Charge automatically',
  emailInvoice: 'Email invoice',
}

export const PlanUpgradeBody: React.FC<PlanUpgradeBodyProps> = ({
  customer,
  plan,
  plans,
  period,
  paymentType,
  autoChargeDisabled,
  onPlanChange,
  onPaymentTypeChange,
}) => {
  const planOptions = plans
    .filter((p) => p.type !== 'enterprise' && p.type !== 'free' && p.type !== 'payAsYouGo' && p.prices.find((price) => price.period === period))
    .map((p) => {
      return { id: p.id, label: p.name }
    })

  const paymentTypeOptions = useMemo(() => {
    const enterpriseBillingPackage = customer.enterpriseSubscription?.enterpriseBillingConfig.package
    if (enterpriseBillingPackage?.type !== 'selfPaid') {
      return []
    }
    const billingOption = enterpriseBillingPackage.billingOptions.find((o) => o.planId === plan.id && o.period === period)
    return billingOption?.paymentTypes.map((paymentType) => ({ id: paymentType, label: PAYMENT_TYPE_LABELS[paymentType] })) ?? []
  }, [customer, plan, period])

  const onPlanOptionSelected = (option: SelectOption) => {
    const newPlan = plans.find((p) => p.id === option.id)
    if (newPlan) {
      onPlanChange(newPlan)
    }
  }

  const onPaymentTypeOptionSelected = (option: SelectOption) => {
    onPaymentTypeChange(option.id)
  }

  return (
    <Container className='p-0'>
      <Row>
        <Col>
          <SelectDropdown
            initialSelectedOption={planOptions.find((p) => p.id === plan.id)}
            options={planOptions}
            className='px-2 py-1 paragraph-small'
            onOptionSelected={onPlanOptionSelected}
          />
          {paymentTypeOptions.length ? (
            <div className='mt-2 d-flex flex-column gap-2'>
              <span className='paragraph-small'>Payment Type</span>
              <SelectDropdown
                className='px-2 py-1 paragraph-small'
                initialSelectedOption={paymentTypeOptions.find((o) => o.id === paymentType)}
                options={paymentTypeOptions}
                disabled={paymentTypeOptions.length === 1}
                onOptionSelected={onPaymentTypeOptionSelected}
              />
              {autoChargeDisabled ? (
                <div className='d-flex flex-column gap-2 align-items-start p-2 zocks-bg-color-warning rounded my-3'>
                  <span className='paragraph-small '>You don't have a payment method yet — please provide one to enable automatic payments.</span>
                  <ChangePaymentInfoButton variant='link' className='button-medium' />
                </div>
              ) : null}
            </div>
          ) : null}
        </Col>
      </Row>
    </Container>
  )
}
